import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnPinningState,
  type ColumnSizingState,
  type FilterFn,
  type Row,
  type RowSelectionState,
  type SortingState,
  type Table,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as React from 'react';
import { FilterBar, type FilterValue } from './filter-bar';
import { TableHeaderCell } from './table-header-cell';
import { DefaultEmptyState, DefaultLoadingState } from './table-states';
import type { PinningShadowState, TableColumnMeta, VirtualTableProps } from './types';
import { buildServerFilter, customFilterFn, getLeftPosition } from './utils';
import { MemoizedVirtualTableBody } from './virtual-table-body';

// Extend the ColumnMeta interface from TanStack Table
declare module '@tanstack/react-table' {
  // biome-ignore lint/correctness/noUnusedVariables: required by TanStack Table API
  interface ColumnMeta<TData, TValue> extends TableColumnMeta {}
}

// ============================================================================
// Main VirtualTable Component
// ============================================================================

export function VirtualTable<TData>({
  data,
  columns,
  enableSorting = false,
  manualSorting = false,
  filterableColumns,
  manualFiltering = false,
  onServerFilter,
  initialFilters,
  columnConfigs,
  onFilterEvent,
  hasActiveFilters = false,
  onClearFilters,
  tableControl,
  filterTrailingContent,
  enableRowSelection = false,
  enableColumnResizing = false,
  columnResizeMode = 'onChange',
  disableKeyboardNavigation = false,
  onCellActivate,
  sorting: controlledSorting,
  onSortingChange,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  columnSizing: controlledColumnSizing,
  onColumnSizingChange,
  columnPinning: controlledColumnPinning,
  onColumnPinningChange,
  estimatedRowHeight = 40,
  overscan = 10,
  height = '100%',
  onRowClick,
  className,
  rowClassName,
  globalFilter,
  onGlobalFilterChange,
  isLoading = false,
  emptyState,
  loadingState,
  stickyHeader = true,
  headerHeight = 48,
  getRowId,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  skeletonRowCount = 50,
  enableColumnReorder = false,
  onColumnReorder,
}: VirtualTableProps<TData>) {
  // Internal state for uncontrolled mode
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({});
  const [internalColumnSizing, setInternalColumnSizing] = React.useState<ColumnSizingState>({});
  const [internalColumnPinning, setInternalColumnPinning] = React.useState<ColumnPinningState>({
    left: [],
    right: [],
  });
  const [internalGlobalFilter, setInternalGlobalFilter] = React.useState('');
  const [pinningShadows, setPinningShadows] = React.useState<PinningShadowState>({
    left: false,
    right: false,
  });

  const keyboardNavigationEnabled = !disableKeyboardNavigation;

  // Merge filterableColumns with column meta icons (filterableColumns icon overrides column meta)
  const resolvedFilterableColumns = React.useMemo(() => {
    if (!filterableColumns) {
      return undefined;
    }
    return filterableColumns.map((filterCol) => {
      const tableColumn = columns.find((col) => col.id === filterCol.value);
      const metaIcon = (tableColumn?.meta as TableColumnMeta)?.icon;
      return {
        ...filterCol,
        icon: filterCol.icon ?? metaIcon,
      };
    });
  }, [filterableColumns, columns]);

  // Handle filter bar changes - only applies complete filters (with condition and value)
  const handleFiltersChange = React.useCallback(
    (filters: FilterValue[]) => {
      // In server-side mode, emit ServerFilter format
      if (manualFiltering && onServerFilter) {
        const serverFilter = buildServerFilter(filters);
        onServerFilter(serverFilter, filters);
        return;
      }

      // Client-side filtering
      const newColumnFilters: ColumnFiltersState = filters
        .filter((f) => f.condition !== null)
        .map((f) => ({
          id: f.columnId,
          value: { condition: f.condition, value: f.value },
        }));
      if (onColumnFiltersChange) {
        onColumnFiltersChange(newColumnFilters);
      } else {
        setInternalColumnFilters(newColumnFilters);
      }
    },
    [onColumnFiltersChange, manualFiltering, onServerFilter],
  );

  // Use controlled or internal state
  const sorting = controlledSorting ?? internalSorting;
  const columnFilters = controlledColumnFilters ?? internalColumnFilters;
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const columnSizing = controlledColumnSizing ?? internalColumnSizing;
  const columnPinning = controlledColumnPinning ?? internalColumnPinning;
  const currentGlobalFilter = globalFilter ?? internalGlobalFilter;

  // Reference to the scroll viewport (from Radix ScrollArea)
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  // Track if ScrollArea viewport is ready
  const [scrollReady, setScrollReady] = React.useState(false);

  // Set ready state when viewport ref is available
  const handleViewportRef = React.useCallback((node: HTMLDivElement | null) => {
    tableContainerRef.current = node;
    if (node) {
      setScrollReady(true);
    }
  }, []);

  React.useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) {
      return;
    }

    let ticking = false;

    const handleScroll = () => {
      if (ticking) {
        return;
      }
      ticking = true;

      requestAnimationFrame(() => {
        const { scrollTop, clientHeight, scrollLeft, scrollWidth } = container;

        const maxScrollLeft = Math.max(0, scrollWidth - clientHeight);
        const nextShadows = {
          left: scrollLeft > 0,
          right: scrollLeft < maxScrollLeft - 1,
        };

        setPinningShadows((prev) =>
          prev.left === nextShadows.left && prev.right === nextShadows.right ? prev : nextShadows,
        );

        if (onLoadMore && hasNextPage && !isFetchingNextPage) {
          // Calculate where real data ends (excluding skeleton rows)
          const realDataHeight = data.length * estimatedRowHeight + headerHeight;
          const viewportBottom = scrollTop + clientHeight;
          // Trigger when viewport bottom is within half a viewport of the skeleton region
          const threshold = clientHeight / 2;
          if (viewportBottom > realDataHeight - threshold) {
            onLoadMore();
          }
        }

        ticking = false;
      });
    };

    handleScroll(); // estado inicial
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    scrollReady,
    data.length,
    estimatedRowHeight,
    headerHeight,
  ]);

  // Create the table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      rowSelection,
      columnSizing,
      columnPinning,
      globalFilter: currentGlobalFilter,
    },
    onSortingChange: onSortingChange ?? setInternalSorting,
    onColumnFiltersChange: onColumnFiltersChange ?? setInternalColumnFilters,
    onRowSelectionChange: onRowSelectionChange ?? setInternalRowSelection,
    onColumnSizingChange: onColumnSizingChange ?? setInternalColumnSizing,
    onColumnPinningChange: onColumnPinningChange ?? setInternalColumnPinning,
    onGlobalFilterChange: onGlobalFilterChange ?? setInternalGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting && !manualSorting && { getSortedRowModel: getSortedRowModel() }),
    ...(filterableColumns && !manualFiltering && { getFilteredRowModel: getFilteredRowModel() }),
    manualSorting,
    manualFiltering,
    enableRowSelection,
    enableColumnResizing,
    columnResizeMode,
    getRowId,
    defaultColumn: {
      minSize: 176,
      maxSize: 500,
      enableResizing: true,
      filterFn: customFilterFn as FilterFn<TData>,
    },
  });

  const { rows } = table.getRowModel();
  const isEmpty = rows.length === 0;

  const totalWidth = table.getTotalSize();

  const columnSizeVars = React.useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing, columns]);

  const columnStartVars = React.useMemo(() => {
    const headers = table.getFlatHeaders();
    const colStarts: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colStarts[`--header-${header.id}-start`] = getLeftPosition(header.column.id, table);
      colStarts[`--col-${header.column.id}-start`] = getLeftPosition(header.column.id, table);
    }
    return colStarts;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing, columns]);

  // DnD for column reordering
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const pinnedColumnIds = React.useMemo(() => {
    const pinned = new Set<string>();
    for (const id of columnPinning?.left ?? []) {
      pinned.add(id);
    }
    for (const id of columnPinning?.right ?? []) {
      pinned.add(id);
    }
    return pinned;
  }, [columnPinning]);

  const sortableColumnIds = React.useMemo(
    () =>
      table
        .getVisibleLeafColumns()
        .map((col) => col.id)
        .filter((id) => !pinnedColumnIds.has(id)),
    [table.getState().columnOrder, columns, pinnedColumnIds],
  );

  const handleColumnDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      const allColumnIds = table.getVisibleLeafColumns().map((col) => col.id);
      const oldIndex = allColumnIds.indexOf(active.id as string);
      const newIndex = allColumnIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const newOrder = arrayMove(allColumnIds, oldIndex, newIndex);
      table.setColumnOrder(newOrder);
      onColumnReorder?.(newOrder);
    },
    [table, onColumnReorder],
  );

  // Column info for loading skeleton
  const loadingColumns = React.useMemo(() => {
    return table.getVisibleLeafColumns().map((col) => ({
      id: col.id,
      size: col.getSize(),
    }));
  }, [table.getState().columnSizingInfo, table.getState().columnSizing, columns]);

  return (
    <div
      className={cn('flex flex-col', className)}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
    >
      {resolvedFilterableColumns && (
        <FilterBar
          className="shrink-0 border-primary-5 border-b"
          columns={resolvedFilterableColumns}
          onFiltersChange={handleFiltersChange}
          initialFilters={initialFilters}
          leadingContent={tableControl}
          trailingContent={filterTrailingContent}
          columnConfigs={columnConfigs}
          onFilterEvent={onFilterEvent}
        />
      )}
      <ScrollArea
        className="virtual-table relative flex-1 bg-white selection:bg-primary-5"
        viewportRef={handleViewportRef}
      >
        {isLoading ? (
          (loadingState ?? (
            <DefaultLoadingState
              columns={loadingColumns}
              headerHeight={headerHeight}
              rowHeight={estimatedRowHeight}
            />
          ))
        ) : (
          <div
            style={{
              minWidth: totalWidth,
              ...columnSizeVars,
              ...columnStartVars,
            }}
          >
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToHorizontalAxis]}
              onDragEnd={handleColumnDragEnd}
            >
              <SortableContext items={sortableColumnIds} strategy={horizontalListSortingStrategy}>
                <div
                  className={cn('flex', stickyHeader && 'sticky top-0 z-dropdown')}
                  style={{ height: headerHeight }}
                >
                  {table.getHeaderGroups().map((headerGroup) => (
                    <div key={headerGroup.id} className="flex">
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHeaderCell
                            key={header.id}
                            header={header}
                            enableColumnResizing={enableColumnResizing}
                            enableColumnReorder={enableColumnReorder}
                            pinningShadows={pinningShadows}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {isEmpty ? (
              <div
                className="sticky left-0 flex items-center justify-center"
                style={{
                  width: tableContainerRef.current?.clientWidth ?? '100%',
                  height: `calc(100% - ${headerHeight}px)`,
                }}
              >
                {emptyState ?? (
                  <DefaultEmptyState
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={onClearFilters}
                  />
                )}
              </div>
            ) : (
              <MemoizedVirtualTableBody
                table={table}
                tableContainerRef={tableContainerRef}
                estimatedRowHeight={estimatedRowHeight}
                overscan={overscan}
                onRowClick={onRowClick}
                rowClassName={rowClassName}
                keyboardNavigationEnabled={keyboardNavigationEnabled}
                onCellActivate={onCellActivate}
                pinningShadows={pinningShadows}
                headerHeight={headerHeight}
                hasNextPage={hasNextPage}
                skeletonRowCount={skeletonRowCount}
              />
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

// ============================================================================
// Re-export types for convenience
// ============================================================================

export type {
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  ColumnPinningState,
  ColumnSizingState,
  Row,
  Table,
  RowSelectionState,
};

export type {
  ServerFilter,
  ServerFilterCondition,
  TableColumnMeta,
  FocusedCell,
  VirtualTableProps,
  TableCellProps,
  PinningShadowState,
} from './types';

export { TableCell } from './table-cell';
